import React, { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { projecoesService } from '../services/api';
import {
  ProjecoesMensaisResponse,
  ProjecoesAnuaisResponse,
  ProjecoesMensaisSerieDiariaItem,
  ProjecoesAnuaisMes,
} from '../types/projecoes';

const getCurrentYearMonth = () => {
  const hoje = new Date();
  return { ano: hoje.getFullYear(), mes: hoje.getMonth() + 1 };
};

const anosDisponiveis = () => {
  const atual = new Date().getFullYear();
  return [atual - 1, atual, atual + 1];
};

const meses = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' },
];

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(1)}%`;
};

const ProjecoesPage: React.FC = () => {
  const { ano: anoAtual, mes: mesAtual } = useMemo(getCurrentYearMonth, []);
  const [ano, setAno] = useState<number>(anoAtual);
  const [mes, setMes] = useState<number>(mesAtual);

  const {
    data: mensal,
    isLoading: loadingMensal,
    error: errorMensal,
  } = useQuery<ProjecoesMensaisResponse, Error>({
    queryKey: ['projecoesMensal', ano, mes],
    queryFn: () => projecoesService.getMensal({ ano, mes }),
  });

  const {
    data: anual,
    isLoading: loadingAnual,
    error: errorAnual,
  } = useQuery<ProjecoesAnuaisResponse, Error>({
    queryKey: ['projecoesAnual', ano],
    queryFn: () => projecoesService.getAnual({ ano }),
  });

  const carregando = loadingMensal || loadingAnual;
  const erro = (errorMensal as any) || (errorAnual as any);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Projeções de Vendas
      </Typography>

      {/* Filtros */}
      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              select
              fullWidth
              label="Ano"
              value={ano}
              onChange={(e) => setAno(Number(e.target.value))}
              size="small"
            >
              {anosDisponiveis().map((a) => (
                <MenuItem key={a} value={a}>
                  {a}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              select
              fullWidth
              label="Mês"
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
              size="small"
            >
              {meses.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Box>

      {carregando && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {erro && !carregando && (
        <Box mb={2}>
          <Alert severity="error">Erro ao carregar projeções. Tente novamente.</Alert>
        </Box>
      )}

      {/* Aviso de configuração incompleta */}
      {mensal && mensal.configWarning && !carregando && !erro && (
        <Box mb={2}>
          <Alert severity="warning">{mensal.configWarning}</Alert>
        </Box>
      )}

      {/* Cards de resumo do mês */}
      {mensal && !carregando && (
        <Box mb={3}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary">
                    {mensal.isMesVigente ? 'Faturamento até hoje' : 'Faturamento do mês'}
                  </Typography>
                  <Typography variant="h6">
                    {mensal.isMesVigente
                      ? formatCurrency(mensal.resumo.faturamentoAteHoje)
                      : formatCurrency(mensal.resumo.faturamentoMesFechado)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary">
                    Meta do mês
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(mensal.resumo.metaFaturamento)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary">
                    {mensal.isMesVigente ? 'Projeção de fechamento' : 'Resultado do mês'}
                  </Typography>
                  <Typography variant="h6">
                    {mensal.isMesVigente
                      ? formatCurrency(mensal.resumo.projecaoFaturamentoMes)
                      : formatCurrency(mensal.resumo.faturamentoMesFechado)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary">
                    % da meta (projetado)
                  </Typography>
                  <Typography variant="h6">
                    {formatPercent(mensal.resumo.percentualMetaProjetado)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Resumo adicional */}
      {mensal && !carregando && (
        <Box mb={3}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary">
                    Pedidos
                  </Typography>
                  <Typography variant="h6">{mensal.resumo.qtdPedidos}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary">
                    Ticket médio
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(mensal.resumo.ticketMedio)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Placeholders para gráficos */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Evolução diária do mês
              </Typography>
              {mensal && mensal.serieDiaria.length > 0 ? (
                <Stack spacing={1} maxHeight={260} sx={{ overflowY: 'auto' }}>
                  {mensal.serieDiaria.map((d: ProjecoesMensaisSerieDiariaItem) => (
                    <Box
                      key={d.dia}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.05)',
                        py: 0.75,
                        px: 0.5,
                        borderRadius: 1,
                        backgroundColor: d.isProjecao ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      }}
                    >
                      <Typography
                        variant="body2"
                        color={d.isProjecao ? 'text.secondary' : 'text.primary'}
                      >
                        Dia {d.dia}
                        {d.isProjecao ? ' (proj.)' : ''}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={d.isProjecao ? 600 : 400}
                        color={d.isProjecao ? 'primary.main' : 'text.primary'}
                      >
                        {formatCurrency(d.faturamento)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Sem dados para o período selecionado.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Visão anual ({ano})
              </Typography>
              {anual && anual.meses.length > 0 ? (
                <Stack spacing={1} maxHeight={260} sx={{ overflowY: 'auto' }}>
                  {anual.meses.map((m: ProjecoesAnuaisMes) => (
                    <Box
                      key={m.mes}
                      display="flex"
                      justifyContent="space-between"
                      sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)', py: 0.5 }}
                    >
                      <Typography variant="body2">
                        {meses.find((mm) => mm.value === m.mes)?.label || m.mes}
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(m.faturamentoReal)}
                        {m.projecao ? ` (proj: ${formatCurrency(m.projecao)})` : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Sem dados para o ano selecionado.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjecoesPage;
